import type { DateArray, DateTime, EventAttributes } from 'ics';
import { createEvent } from 'ics';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import duration from 'dayjs/plugin/duration.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

const WORDS_PER_MINUTE = 200;

export type ReadTime = {
	words: number;
	minutes: number;
	human: string;
};

export type TimezoneInfo = {
	name: string;
	offset: number;
	offsetString: string;
};

export type ParsableDate = string | number | Date | Dayjs | null | undefined;
export default class DateTimeUtils {
	public static parseTz(date: ParsableDate, timezone: string, format: string): Date {
		const asDayJs = dayjs.tz(date, format, timezone);
		return asDayJs.toDate();
	}
	public static parseUtc(date: ParsableDate, format?: string): Date {
		const asDayJs = dayjs.utc(date, format);
		return asDayJs.toDate();
	}

	public static parse(date: ParsableDate, format?: string): Date {
		const asDayJs = dayjs(date, format);
		return asDayJs.toDate();
	}

	/**
	 * A calendar day in CALENDAR_DATE_FORMAT, or null when the text is not exactly one: strict, so
	 * 2024-02-30 is refused rather than rolled into March. A calendar day is not an instant, so it
	 * stays text, with no time or zone.
	 */
	public static parseCalendarDate(value: string): string | null {
		return dayjs.utc(value, CALENDAR_DATE_FORMAT, true).isValid() ? value : null;
	}

	/**
	 * An IANA zone name such as Australia/Brisbane. A bare offset such as +10:00 is refused: it only
	 * describes today, while a name knows each date's daylight saving.
	 */
	public static isNamedTimeZone(value: unknown): value is string {
		if (typeof value !== 'string') {
			return false;
		}
		try {
			const zone = new Intl.DateTimeFormat('en', { timeZone: value }).resolvedOptions().timeZone;
			return !zone.startsWith('+') && !zone.startsWith('-');
		} catch {
			return false;
		}
	}

	/** The calendar day an instant falls on in a time zone, from the platform's zone data. */
	public static calendarDateIn(date: Date, timeZone: string): string {
		// en-CA writes days as YYYY-MM-DD.
		return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
			date,
		);
	}

	/** A calendar day in another format, with no zone involved, so no day a zone skips can move it. */
	public static formatCalendarDate(day: string, format: string): string {
		return dayjs.utc(day, CALENDAR_DATE_FORMAT, true).format(format);
	}

	/** Postgres DATE columns reach Prisma as midnight UTC on that day. */
	public static calendarDateToDb(day: string): Date {
		return new Date(`${day}T00:00:00.000Z`);
	}

	public static calendarDateFromDb(date: Date): string {
		return date.toISOString().slice(0, 10);
	}

	public static getTimezones() {
		return Intl.supportedValuesOf('timeZone');
	}

	public static getTimezoneInfo(): TimezoneInfo {
		const now = dayjs();
		const name = dayjs.tz.guess();
		const offset = now.utcOffset();

		return {
			name,
			offset,
			offsetString: 'GMT' + now.format('Z'),
		};
	}

	public static calculateReadTime(text: string): ReadTime {
		const words = text.split(/\s+/).length;
		const minutes = words / WORDS_PER_MINUTE;
		const human = DateTimeUtils.humanizeDuration(minutes, 'minutes');

		return {
			words,
			minutes,
			human,
		};
	}

	public static humanizeDuration(time: number, unit?: duration.DurationUnitType | undefined): string {
		return dayjs.duration(time, unit).humanize();
	}

	// `ics` writes a string date into the file verbatim, which is not valid iCalendar. Strings without
	// an offset are read as UTC, never the runtime zone; date arrays and epochs keep their own meaning.
	private static toIcsDate(value: DateTime): DateArray | number {
		return Array.isArray(value) || typeof value === 'number' ? value : dayjs.utc(value).valueOf();
	}

	public static createICal(options: EventAttributes, filename: string) {
		const normalised: EventAttributes =
			'end' in options && options.end !== undefined
				? {
						...options,
						start: DateTimeUtils.toIcsDate(options.start),
						end: DateTimeUtils.toIcsDate(options.end),
					}
				: { ...options, start: DateTimeUtils.toIcsDate(options.start) };

		return new Promise<File>((resolve, reject) => {
			createEvent(normalised, (error, value) => {
				if (error) {
					reject(error);
				} else {
					resolve(new File([value], filename, { type: 'text/calendar' }));
				}
			});
		});
	}

	public static format(date: ParsableDate, format: string, convertToTimezone?: string): string {
		const asDayJs = convertToTimezone ? dayjs(date).tz(convertToTimezone) : dayjs(date);
		return asDayJs.format(format);
	}

	public static billingMonthsFromDays(days: number): number {
		const d = Math.max(1, days);
		return Math.max(1, Math.round(d / 30));
	}
}

export const DATE_FORMAT = 'DD-MM-YYYY';
export const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_TIME_FORMAT = 'DD-MM-YYYY hh:mm A';

export const DATE_HUMAN_FORMAT = 'Do [of] MMM YYYY';
export const DATE_HUMAN_SHORT_FORMAT = 'D MMM';
export const TIME_HUMAN_FORMAT = 'h:mm a';

export const DATE_TIME_SHORT_FORMAT = 'Do MMM @ h:mm a';
